import { ReactNode } from 'react';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  title?: string;
  description?: string;
  showDetails?: boolean;
  showHomeButton?: boolean;
  children?: ReactNode;
}

/**
 * ErrorFallback Component
 *
 * A reusable, customizable error fallback UI component that can be used
 * with ErrorBoundary or standalone error states.
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={<ErrorFallback />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * @example Custom fallback
 * ```tsx
 * <ErrorFallback
 *   title="Failed to load data"
 *   description="We couldn't fetch the latest information."
 *   resetError={() => refetch()}
 *   showHomeButton={false}
 * />
 * ```
 */
export function ErrorFallback({
  error,
  resetError,
  title = 'Something went wrong',
  description = 'We encountered an unexpected error. Please try again or contact support if the problem persists.',
  showDetails = process.env.NODE_ENV === 'development',
  showHomeButton = true,
  children,
}: ErrorFallbackProps) {
  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <div className="card max-w-2xl w-full">
        <div className="flex items-start gap-4 mb-6">
          {/* Error Icon */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
          </div>

          <div className="flex-1" role="alert" aria-live="polite" aria-atomic="true">
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">{title}</h2>
            <p className="text-neutral-600">{description}</p>
          </div>
        </div>

        {/* Custom children content */}
        {children && <div className="mb-6">{children}</div>}

        {/* Error Details (only in development or when explicitly shown) */}
        {showDetails && error && (
          <details className="mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
            <summary className="cursor-pointer text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors">
              Error Details
            </summary>
            <div className="mt-3 space-y-2">
              <div>
                <p className="text-xs font-medium text-neutral-600 mb-1">Message:</p>
                <p className="text-sm font-mono text-red-600 break-all">
                  {error.message}
                </p>
              </div>
              {error.stack && (
                <div>
                  <p className="text-xs font-medium text-neutral-600 mb-1">Stack Trace:</p>
                  <pre className="text-xs text-neutral-600 overflow-x-auto bg-white p-2 rounded border border-neutral-200 max-h-64">
                    {error.stack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {resetError && (
            <button
              onClick={resetError}
              className="btn-primary inline-flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
              Try Again
            </button>
          )}

          {showHomeButton && (
            <a
              href="/"
              className="btn-secondary inline-flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                />
              </svg>
              Go Home
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
